import { useState } from 'react';
import { RefreshCw, Cloud, CloudOff, AlertCircle, CheckCircle, Bell, BellOff } from 'lucide-react';

import { useAuthStore } from '@/stores/auth-store';
import { userService } from '@/services/user-service';
import { useTheme } from '@/hooks/useTheme';
import { runSync } from '@/services/sync-service';

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'offline';
import { StorageUsageIndicator } from '@/components/photos/StorageUsageIndicator';
import { STATE_NAME_OPTIONS } from '@/utils/us-states';

const inputBase =
  'w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded text-[var(--color-text-primary)] text-body-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors duration-100';

const labelBase =
  'block field-label mb-1';

const sectionClass =
  'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 space-y-4';

// ─── Sync Status Badge ────────────────────────────────────────────────────────

function formatAgo(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} hr ago`;
}

interface SyncStatusBadgeProps {
  status: SyncStatus;
  lastSyncedAt: Date | null;
  errorMessage: string | null;
}

function SyncStatusBadge({ status, lastSyncedAt, errorMessage }: SyncStatusBadgeProps) {

  if (status === 'syncing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-body-xs text-[var(--color-accent)]">
        <RefreshCw size={12} className="animate-spin" />
        Syncing…
      </span>
    );
  }
  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1.5 text-body-xs text-[var(--color-text-muted)]">
        <CheckCircle size={12} className="text-[var(--color-done)]" />
        {lastSyncedAt ? `Last synced: ${formatAgo(lastSyncedAt)}` : 'Synced'}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-body-xs text-[var(--color-accent)]" title={errorMessage ?? undefined}>
        <AlertCircle size={12} />
        Sync failed — tap retry
      </span>
    );
  }
  if (status === 'offline') {
    return (
      <span className="inline-flex items-center gap-1.5 text-body-xs text-[var(--color-text-muted)]">
        <CloudOff size={12} />
        Offline · Changes queued
      </span>
    );
  }
  // idle
  return (
    <span className="inline-flex items-center gap-1.5 text-body-xs text-[var(--color-text-muted)]">
      <Cloud size={12} />
      Not yet synced
    </span>
  );
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const preferences = useAuthStore((s) => s.preferences);
  const updatePreferences = useAuthStore((s) => s.updatePreferences);
  const { theme, setTheme } = useTheme();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [defaultState, setDefaultState] = useState(user?.defaultState ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync state
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Notification state
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'denied',
  );

  const notificationsEnabled = preferences?.notificationsEnabled ?? false;
  const isIosWithoutStandalone =
    /iPhone|iPad|iPod/.test(navigator.userAgent) &&
    !window.matchMedia('(display-mode: standalone)').matches;

  function handleStateChange(state: string) {
    setDefaultState(state);
  }

  async function handleSave() {
    if (!user) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await userService.updateProfile({
        displayName,
        defaultState,
      });
      useAuthStore.setState({ user: updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSyncNow() {
    if (isSyncing || !user) return;
    setIsSyncing(true);
    setSyncStatus('syncing');
    try {
      const result = await runSync(user.id);
      setSyncStatus('success');
      setLastSyncedAt(new Date(result.lastSyncedAt));
      setSyncError(null);
    } catch (err) {
      setSyncStatus(navigator.onLine ? 'error' : 'offline');
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleNotificationToggle() {
    if (notificationsEnabled) {
      await updatePreferences({ notificationsEnabled: false });
      return;
    }

    if (typeof Notification === 'undefined') return;

    setIsRequestingPermission(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await updatePreferences({ notificationsEnabled: true });
        setPermissionDenied(false);
      } else {
        setPermissionDenied(permission === 'denied');
      }
    } finally {
      setIsRequestingPermission(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2">
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">Profile</h1>
      </div>

      {/* ── Account details ───────────────────────────────────────────── */}
      <section className={sectionClass}>
        {/* Email — read-only */}
        <div>
          <label className={labelBase}>Email</label>
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-elevated)] border border-[var(--color-border)] rounded">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[var(--color-text-muted)] shrink-0"
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="text-body-sm text-[var(--color-text-secondary)]">
              {user?.email ?? '—'}
            </span>
          </div>
          <p className="mt-1 text-body-xs text-[var(--color-text-muted)]">
            Managed by your account
          </p>
        </div>

        {/* Display name */}
        <div>
          <label htmlFor="settings-displayName" className={labelBase}>
            Display name
          </label>
          <input
            id="settings-displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputBase}
            placeholder="Your name"
          />
        </div>

        {/* Default state */}
        <div>
          <label htmlFor="settings-defaultState" className={labelBase}>
            Default state
          </label>
          <select
            id="settings-defaultState"
            value={defaultState}
            onChange={(e) => handleStateChange(e.target.value)}
            className={inputBase}
          >
            <option value="">Select a state</option>
            {STATE_NAME_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Error message */}
        {error !== null && (
          <p className="text-body-sm text-[var(--color-danger)]">{error}</p>
        )}

        {/* Save button */}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={isSaving || displayName.trim() === ''}
            className="px-4 py-2 bg-[var(--color-accent)] text-white rounded text-body-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saved ? 'Saved!' : isSaving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </section>

      {/* ── Theme ─────────────────────────────────────────────────────── */}
      <section className={sectionClass}>
        <span className={labelBase}>Theme</span>
        <div
          role="radiogroup"
          aria-label="App theme"
          className="flex gap-1 bg-[var(--color-bg)] rounded-lg p-1"
        >
          {([
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'system', label: 'System' },
          ] as const).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={theme === value}
              onClick={() => void setTheme(value)}
              className={`flex-1 py-1.5 rounded-md text-body-sm font-medium transition-colors duration-100 ${
                theme === value
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Notifications ─────────────────────────────────────────────── */}
      <section className={sectionClass}>
        <span className={labelBase}>Notifications</span>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {notificationsEnabled ? (
              <Bell size={16} className="text-[var(--color-accent)]" />
            ) : (
              <BellOff size={16} className="text-[var(--color-text-muted)]" />
            )}
            <span className="text-body-sm text-[var(--color-text-primary)]">
              Push notifications
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notificationsEnabled}
            onClick={() => void handleNotificationToggle()}
            disabled={isRequestingPermission}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] disabled:opacity-50 ${
              notificationsEnabled ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-elevated)]'
            }`}
            aria-label="Toggle push notifications"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform transition-transform duration-200 ${
                notificationsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {permissionDenied && (
          <p className="text-body-xs text-[var(--color-text-muted)]">
            Notifications blocked by the browser. Enable them in your browser settings to receive alerts.
          </p>
        )}

        {isIosWithoutStandalone && (
          <p className="text-body-xs text-[var(--color-text-muted)]">
            iOS requires Ridgen to be installed to your home screen for push notifications.
          </p>
        )}

        {notificationsEnabled && (
          <div>
            <label htmlFor="notification-lead-days" className={labelBase}>
              Alert lead time
            </label>
            <select
              id="notification-lead-days"
              value={preferences?.notificationLeadDays ?? 3}
              onChange={(e) => void updatePreferences({ notificationLeadDays: Number(e.target.value) })}
              className={inputBase}
            >
              {[1, 2, 3, 5, 7, 10, 14].map((days) => (
                <option key={days} value={days}>
                  {days} {days === 1 ? 'day' : 'days'} before
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      {/* ── Cloud Sync ────────────────────────────────────────────────── */}
      <section className={sectionClass}>
        <span className={labelBase}>Cloud Sync</span>

        <div className="flex items-center justify-between gap-3">
          <SyncStatusBadge
            status={syncStatus}
            lastSyncedAt={lastSyncedAt}
            errorMessage={syncError}
          />
          <button
            type="button"
            onClick={() => void handleSyncNow()}
            disabled={isSyncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] hover:border-[var(--color-accent)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={13} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? 'Syncing…' : 'Sync Now'}
          </button>
        </div>
      </section>

      {/* ── Storage ───────────────────────────────────────────────────── */}
      <section className={sectionClass}>
        <span className={labelBase}>Local Storage</span>
        {user && <StorageUsageIndicator userId={user.id} />}
      </section>
    </div>
  );
}
